import { Resolve, Router } from "@angular/router";
import { EMPTY, Observable, catchError, of, switchMap, tap } from "rxjs";
import {
  ParentPermissions,
  ChildPermissions,
  Roles,
  StatusService,
} from "../services/user/status.service";
import { Injectable } from "@angular/core";
import { UIService } from "../services/ui/ui.service";
import { AuthService } from "../services/auth/auth.service";
import { Message } from "../models/message";

@Injectable({ providedIn: "root" })
export class PermissionsResolver implements Resolve<any> {
  constructor(
    private statusSvc: StatusService,
    private uiSvc: UIService,
    private authSvc: AuthService,
    private router: Router
  ) {}

  resolve(): Observable<any> {
    if (!navigator.onLine) {
      return of(null);
    }
    this.statusSvc.permissionsLoading$.next(true);

    return this.statusSvc.getStatus().pipe(
        tap((response) => {
          let user = response;
          if (user) {
            sessionStorage.setItem('userInfo', JSON.stringify(user));
          }
        }),
      switchMap((response) => {
        let user = response;
        if (
          user?.userStatus == "Completed" ||
          user?.userStatus == "PendingForApproval" || user?.userStatus == "Active"
        ) {
          return this.statusSvc.getRole();
        } else if (
          user?.userStatus == "Init" ||
          user?.userStatus == "Verified"
        ) {
          this.router.navigate(["/verification"]);
          of(null);
        }
        // If both conditions fail, logout the user
        throw new Error();
      }),
      tap((response: any) => {
        if (response) {
          this.handlePermissions(response ?? []);
        }
        this.statusSvc.permissionsLoading$.next(false);
      }),
      catchError((err) => {
        this.authSvc.logoutUser();
        this.statusSvc.permissionsLoading$.next(false);
        let msg = new Message();
        msg.msg =
          "Permissions not found, please re-login or contact administrator";
        msg.iconType = "error";
        msg.type = "danger";
        this.uiSvc.showToast(msg);
        return EMPTY;
      })
    );
  }

  handlePermissions(roles: Roles[]) {
    let rolePermissionCodes: string[] = [];
    let parentPermissions: ParentPermissions = new ParentPermissions();
    let childPermissions: ChildPermissions = new ChildPermissions();

    var i = 0;
    for (var index = 0; index < roles.length; index++) {
      for (var index2 = 0; index2 < roles[index].permissions.length; index2++) {
        rolePermissionCodes[i] =
          roles[index].permissions[index2].permissionCode;
        i++;
      }
    }

    for (var index = 0; index < rolePermissionCodes.length; index++) {
      var role = rolePermissionCodes[index];

      if (role == "ViewAndEditProfile") {
        parentPermissions.ViewAndEditProfile = true;
        parentPermissions.Library = true;
        childPermissions.BrowseLibrary = true;
      }

      if (role == "UsersManagement" || role == "RoleManagement") {
        parentPermissions.UsersRolesManage = true;

        if (role == "UsersManagement") {
          childPermissions.UsersManagement = true;
        }

        if (role == "RoleManagement") {
          childPermissions.RoleManagement = true;
        }
      }

      if (role == "ManageReportTemplates") {
        parentPermissions.ManageReportTemplates = true;
      }

      if (role == "ManageClients") {
        parentPermissions.ManageClients = true;
      }

      if (role == "ManageEntities") {
        parentPermissions.ManageEntities = true;
      }

      if (role == "Technician") {
        parentPermissions.Technician = true;
        childPermissions.DraftReports = true;
        childPermissions.SubmittedReports = true;
        childPermissions.PastReports = true;
        childPermissions.WorkVoucher = true;
      }

      if (role == "Supervisor") {
        parentPermissions.Supervisor = true;
        childPermissions.DraftReports = true;
        childPermissions.SubmittedReports = true;
        // childPermissions.ReviewReports = true;
        childPermissions.PastReports = true;
        childPermissions.WorkVoucher = true;
      }

      if (role == "Manager") {
        parentPermissions.Manager = true;
        childPermissions.DraftReports = true;
        childPermissions.SubmittedReports = true;
        // childPermissions.ReviewReports = true;
        childPermissions.ApproveReports = true;
        childPermissions.PastReports = true;
        childPermissions.WorkVoucher = true;
      }

      if (role == "Accountant") {
        parentPermissions.Accountant = true;
        childPermissions.CompletedReports = true;
        childPermissions.CompletedWorkSheets = true;
        childPermissions.WorkVoucher = true;
      }

      if (role == "ReportMaintainer") {
        parentPermissions.ReportMaintainer = true;
        childPermissions.AllWorkSheets = true;
        childPermissions.AllWorkReports = true;
        childPermissions.AllWorkVouchers = true;
      }

      if (role == "LibraryManagement") {
        parentPermissions.Library = true;
        childPermissions.BrowseLibrary = true;
        childPermissions.ManageLibrary = true;
      }
    }

    this.statusSvc.setPermissionCodesParent(parentPermissions);
    this.statusSvc.setPermissionCodesChild(childPermissions);
  }
}
